'use client';

import type { MediaResponseDto } from '@/lib/api/clients/properties';
import { ChevronLeft, ChevronRight, Download, Share2, X, ZoomIn, ZoomOut } from 'lucide-react';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';

interface PropertyGalleryLightboxProps {
  images: MediaResponseDto[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
  propertyName: string;
}

export function PropertyGalleryLightbox({
  images,
  initialIndex,
  isOpen,
  onClose,
  propertyName,
}: PropertyGalleryLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  // Reset zoom when changing images
  useEffect(() => {
    setIsZoomed(false);
  }, [currentIndex]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, goToPrevious, goToNext]);

  // Touch handlers for swipe
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientX || null);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientX || null);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) goToNext();
    if (isRightSwipe) goToPrevious();
  };

  const handleDownload = () => {
    const currentImage = images[currentIndex];
    if (!currentImage) return;
    const link = document.createElement('a');
    link.href = currentImage.url;
    link.download = `${propertyName}-${currentIndex + 1}.jpg`;
    link.click();
  };

  const handleShare = async () => {
    const currentImage = images[currentIndex];
    if (!currentImage) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: propertyName,
          text: `Check out this image of ${propertyName}`,
          url: currentImage.url,
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }
  };

  if (!isOpen) return null;

  const currentImage = images[currentIndex];
  if (!currentImage) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm">
      {/* Header Bar */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4 md:p-6">
        <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Close gallery"
            >
              <X size={24} />
            </button>
            <div className="hidden md:block">
              <p className="text-white font-semibold text-lg">{propertyName}</p>
              <p className="text-white/70 text-sm">
                {currentIndex + 1} of {images.length}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <p className="md:hidden text-white/90 text-sm font-medium">
              {currentIndex + 1} / {images.length}
            </p>
            <button
              onClick={() => setIsZoomed(!isZoomed)}
              className="p-2 text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label={isZoomed ? 'Zoom out' : 'Zoom in'}
            >
              {isZoomed ? <ZoomOut size={20} /> : <ZoomIn size={20} />}
            </button>
            <button
              onClick={handleShare}
              className="hidden md:block p-2 text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Share image"
            >
              <Share2 size={20} />
            </button>
            <button
              onClick={handleDownload}
              className="hidden md:block p-2 text-white hover:bg-white/10 rounded-full transition-colors"
              aria-label="Download image"
            >
              <Download size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Image Area */}
      <div 
        className="absolute inset-0 flex items-center justify-center p-4 md:p-20"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className={`relative w-full h-full transition-transform duration-300 ${isZoomed ? 'scale-150 cursor-move' : 'scale-100'}`}>
          <Image
            src={currentImage.url}
            alt={`${propertyName} - Image ${currentIndex + 1}`}
            fill
            className="object-contain"
            sizes="100vw"
            priority
            quality={95}
          />
        </div>
      </div>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 md:p-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full transition-all hover:scale-110 z-10"
            aria-label="Previous image"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 md:p-4 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white rounded-full transition-all hover:scale-110 z-10"
            aria-label="Next image"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {/* Thumbnail Strip */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 md:p-6">
        <div className="max-w-screen-2xl mx-auto overflow-x-auto scrollbar-thin scrollbar-thumb-white/30">
          <div className="flex gap-2 md:gap-3 justify-center min-w-max px-4">
            {images.map((image, index) => (
              <button
                key={image.id}
                onClick={() => setCurrentIndex(index)}
                className={`relative flex-shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden transition-all ${
                  index === currentIndex
                    ? 'ring-2 ring-white scale-110'
                    : 'opacity-50 hover:opacity-100'
                }`}
              >
                <Image
                  src={image.thumbnailUrl || image.url}
                  alt={`Thumbnail ${index + 1}`}
                  fill
                  className="object-cover"
                  sizes="80px"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
